/**
 * TrainingForm Component
 * Form for creating/editing trainings
 *
 * @version 2025.2.0
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Autocomplete,
  Alert,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import {
  TrainingFormData,
  TrainingFormProps,
  TrainingStatus,
  TrainingStatusLabels,
  TrainingNRTypeLabels,
  TrainingCategory,
  TrainingCategoryLabels,
} from '../../../types/training';
import { TrainingTypeMinimal, TrainingTypeData } from '../../../types/trainingType';
import { trainingTypeService } from '../../../services/trainingTypeService';
import { employeeService } from '../../../services/employeeService';

interface EmployeeOption {
  id: number;
  name: string;
  cpf?: string;
  companyName?: string;
}

export const TrainingForm: React.FC<TrainingFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const isEditing = Boolean(initialData?.id);
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [loadingDependencies, setLoadingDependencies] = useState(true);
  const [trainingTypes, setTrainingTypes] = useState<TrainingTypeMinimal[]>([]);
  const [selectedTrainingType, setSelectedTrainingType] = useState<TrainingTypeData | null>(null);

  // Whether the record is a legacy record (no trainingTypeId)
  const isLegacyRecord = isEditing && !initialData?.trainingTypeId;

  const today = new Date().toISOString().split('T')[0];
  const twoYearsLater = new Date(Date.now() + 2 * 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<TrainingFormData>({
    defaultValues: {
      employeeId: initialData?.employeeId ?? 0,
      departmentId: initialData?.departmentId,
      occupationId: initialData?.occupationId,
      instructorId: initialData?.instructorId,
      trainingTypeId: initialData?.trainingTypeId,
      name: initialData?.name ?? '',
      type: initialData?.type,
      category: initialData?.category ?? TrainingCategory.INITIAL,
      description: initialData?.description ?? '',
      activityDescription: initialData?.activityDescription ?? '',
      workload: initialData?.workload,
      completedAt: initialData?.completedAt ?? today,
      expiresAt: initialData?.expiresAt ?? twoYearsLater,
      certificatePath: initialData?.certificatePath ?? '',
      esocialCode: initialData?.esocialCode ?? '',
      sendToEsocial: initialData?.sendToEsocial ?? false,
      status: initialData?.status ?? TrainingStatus.ACTIVE,
    },
  });

  const completedAt = watch('completedAt');
  const trainingTypeId = watch('trainingTypeId');

  // Load employees from all tenant companies
  useEffect(() => {
    const loadDependencies = async () => {
      try {
        setLoadingDependencies(true);
        const response = await employeeService.getEmployees({
          limit: 1000,
        });
        const mapped: EmployeeOption[] = (response.data ?? []).map((e) => ({
          id: e.id,
          name: e.name,
          cpf: e.cpf,
          companyName: e.company?.tradeName || e.company?.legalName,
        }));
        setEmployees(mapped);
      } catch (error) {
        console.error('Error loading dependencies:', error);
      } finally {
        setLoadingDependencies(false);
      }
    };
    loadDependencies();
  }, []);

  // Load all active training types (global entity)
  useEffect(() => {
    trainingTypeService.getMinimal().then(setTrainingTypes).catch(console.error);
  }, []);

  // Load full training type data when editing a record that has trainingTypeId
  useEffect(() => {
    if (initialData?.trainingTypeId && !selectedTrainingType) {
      trainingTypeService.getById(initialData.trainingTypeId)
        .then(setSelectedTrainingType)
        .catch(console.error);
    }
  }, [initialData?.trainingTypeId, selectedTrainingType]);

  // Recalculate expiresAt when completedAt changes and a training type with periodicityMonths is selected
  useEffect(() => {
    if (selectedTrainingType?.periodicityMonths && completedAt) {
      const completed = new Date(completedAt);
      completed.setMonth(completed.getMonth() + selectedTrainingType.periodicityMonths);
      setValue('expiresAt', completed.toISOString().split('T')[0]);
    }
  }, [completedAt, selectedTrainingType, setValue]);

  const handleTrainingTypeChange = useCallback(async (_event: React.SyntheticEvent, newValue: TrainingTypeMinimal | null) => {
    if (newValue) {
      setValue('trainingTypeId', newValue.id);
      try {
        const fullType = await trainingTypeService.getById(newValue.id);
        setSelectedTrainingType(fullType);
        // Auto-fill name (denormalized)
        setValue('name', fullType.name);
        // Auto-fill workload
        if (fullType.workload) {
          setValue('workload', fullType.workload);
        }
        // Auto-fill esocialCode
        if (fullType.esocialCode) {
          setValue('esocialCode', fullType.esocialCode);
        }
        // Calculate expiresAt if periodicityMonths and completedAt are set
        const currentCompletedAt = watch('completedAt');
        if (fullType.periodicityMonths && currentCompletedAt) {
          const completed = new Date(currentCompletedAt);
          completed.setMonth(completed.getMonth() + fullType.periodicityMonths);
          setValue('expiresAt', completed.toISOString().split('T')[0]);
        }
      } catch (error) {
        console.error('Error loading training type details:', error);
      }
    } else {
      setValue('trainingTypeId', undefined);
      setValue('name', '');
      setSelectedTrainingType(null);
    }
  }, [setValue, watch]);

  const onFormSubmit = (data: TrainingFormData) => {
    onSubmit(data);
  };

  if (loadingDependencies) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <form onSubmit={handleSubmit(onFormSubmit)}>
      <Grid container spacing={3}>
        {/* Employee */}
        <Grid size={12}>
          <Card>
            <CardHeader title="Funcionário" />
            <CardContent>
              <Grid container spacing={2}>
                {/* Employee Selection */}
                <Grid size={12}>
                  <Controller
                    name="employeeId"
                    control={control}
                    rules={{ required: 'Funcionário é obrigatório', min: { value: 1, message: 'Selecione um funcionário' } }}
                    render={({ field }) => (
                      <Autocomplete
                        options={employees}
                        getOptionLabel={(option) => option.name || ''}
                        value={employees.find(e => e.id === field.value) ?? null}
                        onChange={(_, newValue) => {
                          field.onChange(newValue?.id ?? 0);
                        }}
                        renderOption={(props, option) => (
                          <li {...props} key={option.id}>
                            <Box>
                              <Typography variant="body2">{option.name}</Typography>
                              {option.companyName && (
                                <Typography variant="caption" color="text.secondary">
                                  {option.companyName}
                                </Typography>
                              )}
                            </Box>
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Funcionário *"
                            error={!!errors.employeeId}
                            helperText={errors.employeeId?.message}
                          />
                        )}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        disabled={isEditing}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Training Information */}
        <Grid size={12}>
          <Card>
            <CardHeader title="Dados do Treinamento" />
            <CardContent>
              <Grid container spacing={2}>
                {/* Legacy record info */}
                {isLegacyRecord && (
                  <Grid size={12}>
                    <Alert severity="info" sx={{ mb: 1 }}>
                      Registro antigo — Nome: <strong>{initialData?.name}</strong>
                      {initialData?.type && (
                        <>, Tipo: <strong>{TrainingNRTypeLabels[initialData.type]}</strong></>
                      )}
                      . Selecione um Tipo de Treinamento abaixo para atualizar.
                    </Alert>
                  </Grid>
                )}

                {/* Training Type Autocomplete */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Autocomplete
                    options={trainingTypes}
                    getOptionLabel={(option) => option.name}
                    value={trainingTypes.find(t => t.id === trainingTypeId) || null}
                    onChange={handleTrainingTypeChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={isLegacyRecord ? 'Tipo de Treinamento' : 'Tipo de Treinamento *'}
                        placeholder="Selecione o tipo de treinamento"
                        error={!!errors.trainingTypeId}
                        helperText={errors.trainingTypeId?.message}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    noOptionsText="Nenhum tipo encontrado"
                  />
                  {/* Hidden name field - auto-populated from training type */}
                  <Controller
                    name="name"
                    control={control}
                    rules={isLegacyRecord ? undefined : { required: 'Selecione um tipo de treinamento' }}
                    render={({ field }) => (
                      <input type="hidden" {...field} />
                    )}
                  />
                  <Controller
                    name="trainingTypeId"
                    control={control}
                    rules={isLegacyRecord ? undefined : { required: 'Tipo de treinamento é obrigatório' }}
                    render={({ field }) => (
                      <input type="hidden" value={field.value ?? ''} onChange={field.onChange} />
                    )}
                  />
                </Grid>

                {/* Category */}
                <Grid size={{ xs: 12, md: 3 }}>
                  <Controller
                    name="category"
                    control={control}
                    rules={{ required: 'Categoria é obrigatória' }}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.category}>
                        <InputLabel>Categoria *</InputLabel>
                        <Select {...field} label="Categoria *">
                          {Object.entries(TrainingCategoryLabels).map(([key, label]) => (
                            <MenuItem key={key} value={key}>{label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>

                {/* Description */}
                <Grid size={12}>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Descrição"
                        fullWidth
                        multiline
                        rows={2}
                      />
                    )}
                  />
                </Grid>

                {/* Activity Description */}
                <Grid size={12}>
                  <Controller
                    name="activityDescription"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Atividades Abordadas"
                        fullWidth
                        multiline
                        rows={2}
                        placeholder="Descreva as atividades abordadas no treinamento"
                      />
                    )}
                  />
                </Grid>

                {/* Workload */}
                <Grid size={{ xs: 12, md: 3 }}>
                  <Controller
                    name="workload"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="number"
                        label="Carga Horária (horas)"
                        fullWidth
                        inputProps={{ min: 1 }}
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                      />
                    )}
                  />
                </Grid>

                {/* eSocial Code */}
                <Grid size={{ xs: 12, md: 3 }}>
                  <Controller
                    name="esocialCode"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Código eSocial (Tabela 28)"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Dates */}
        <Grid size={12}>
          <Card>
            <CardHeader title="Datas" />
            <CardContent>
              <Grid container spacing={2}>
                {/* Completed At */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Controller
                    name="completedAt"
                    control={control}
                    rules={{ required: 'Data de realização é obrigatória' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="date"
                        label="Data de Realização *"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        error={!!errors.completedAt}
                        helperText={errors.completedAt?.message}
                      />
                    )}
                  />
                </Grid>

                {/* Expires At */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Controller
                    name="expiresAt"
                    control={control}
                    rules={{ required: 'Data de validade é obrigatória' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="date"
                        label="Data de Validade *"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        error={!!errors.expiresAt}
                        helperText={errors.expiresAt?.message}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* eSocial & Status */}
        <Grid size={12}>
          <Card>
            <CardHeader title="eSocial e Status" />
            <CardContent>
              <Grid container spacing={2}>
                {/* Send to eSocial */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Controller
                    name="sendToEsocial"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            {...field}
                            checked={field.value}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body1">
                              Enviar para eSocial
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Evento S-2245 - Treinamentos, Capacitações
                            </Typography>
                          </Box>
                        }
                      />
                    )}
                  />
                </Grid>

                {/* Status */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select {...field} label="Status">
                          {Object.entries(TrainingStatusLabels).map(([key, label]) => (
                            <MenuItem key={key} value={key}>{label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Action Buttons */}
        <Grid size={12}>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={onCancel}
              startIcon={<CancelIcon />}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
              disabled={loading}
            >
              {loading ? 'Salvando...' : isEditing ? 'Atualizar' : 'Cadastrar'}
            </Button>
          </Box>
        </Grid>
      </Grid>

    </form>
  );
};

export default TrainingForm;
